// Browse all clothing items by category
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ProductCard } from '../../components/shopper/ProductCard';
import { ShopperNavActions } from '../../components/shopper/ShopperNavActions';
import { useSmartBack } from '../../hooks/useSmartBack';
import { SHOPPER_HOME } from '../../constants/routes';
import { useSearch, SearchFilters, SearchProduct } from '../../hooks/useSearch';
import { useCartContext } from '../../hooks/useCart';
import { useFavoritesContext } from '../../hooks/useFavorites';
import { FASH_LOGO } from '../../constants/brand';

const PRODUCT_CATEGORIES = [
  'Dresses',
  'Tops',
  'Bottoms',
  'Outerwear',
  'Swimwear',
  'Activewear',
  'Accessories',
  'Shoes',
  'Jewelry',
  'Bags',
  'Jumpsuits',
  'Lingerie',
  'Sets',
  'Other',
];

export function CategoriesPage() {
  const navigate = useNavigate();
  const goBack = useSmartBack(SHOPPER_HOME);
  const cart = useCartContext();
  const favorites = useFavoritesContext();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const filters: SearchFilters = useMemo(
    () => (selectedCategory ? { category: selectedCategory } : {}),
    [selectedCategory]
  );

  const { results, loading } = useSearch({
    debounceMs: 200,
    hitsPerPage: 60,
    filters,
  });

  const hits = results?.hits ?? [];
  const totalHits = results?.totalHits ?? 0;

  const handleProductClick = (id: string) =>
    navigate(`/product/${id}`, { state: { from: '/categories' } });
  const handleAddToCart = (product: SearchProduct, selectedSize?: string) => {
    const size = product.sizes?.find((item) => item.label === selectedSize && item.available);
    if (!size) {
      navigate(`/product/${product.id}`, { state: { from: '/categories' } });
      return;
    }
    cart.addItem({
      productId: product.id,
      name: product.name,
      brand: product.brand,
      businessId: product.brandId || '',
      price: product.price,
      size: size.label,
      quantity: 1,
      imageUrl: product.imageUrl || product.images?.[0] || '',
    });
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => (selectedCategory ? setSelectedCategory(null) : goBack())}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
              aria-label={selectedCategory ? 'Back to categories' : 'Go back'}
            >
              <ArrowLeft className="h-5 w-5 text-heading" />
            </button>
            <Link to={SHOPPER_HOME} className="flex-1 flex justify-center">
              <img src={FASH_LOGO} alt="Fash" className="h-10 object-contain" />
            </Link>
            <ShopperNavActions className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0" iconClassName="h-5 w-5 text-heading" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-2xl md:text-3xl font-serif text-heading mb-2">
          {selectedCategory ? selectedCategory : 'Categories'}
        </h1>
        <p className="text-sm text-body mb-6">
          {selectedCategory
            ? `Browse ${totalHits} item${totalHits !== 1 ? 's' : ''} in ${selectedCategory}`
            : 'Choose a category to see all items.'}
        </p>

        {!selectedCategory ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {PRODUCT_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className="p-4 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-primary/30 text-heading font-medium text-sm text-center transition-colors"
              >
                {cat}
              </button>
            ))}
          </div>
        ) : (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : hits.length === 0 ? (
              <div className="text-center py-16 text-body">
                <p>No items in this category yet.</p>
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="mt-4 text-primary font-medium hover:underline"
                >
                  Back to categories
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {hits.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => handleProductClick(product.id)}
                    className="cursor-pointer"
                  >
                    <ProductCard
                      product={{
                        id: product.id,
                        name: product.name,
                        brand: product.brand,
                        price: product.price,
                        compareAtPrice: product.compareAtPrice,
                        imageUrl: product.imageUrl || product.images?.[0] || '',
                      sizes: product.sizes,
                      }}
                    onAddToCart={(size, e) => {
                        e?.stopPropagation?.();
                      handleAddToCart(product, size);
                      }}
                      onFavorite={() =>
                        favorites.toggleFavorite({
                          productId: product.id,
                          name: product.name,
                          brand: product.brand,
                          price: product.price,
                          imageUrl: product.imageUrl || product.images?.[0] || '',
                        })
                      }
                      isFavorited={favorites.isFavorited(product.id)}
                      compact
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
